import { useEffect, useRef } from "react";

// Two-finger pinch on the camera viewport → zoom. `getEl` returns the DOM node
// (re-queried when `zoom` appears), `zoom` is {min,max,step,value} or null.
export function usePinchZoom(getEl, zoom, onChange, enabled = true) {
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const hasZoom = !!zoom;

  useEffect(() => {
    if (!enabled || !hasZoom) return undefined;
    const el = getEl();
    if (!el) return undefined;
    let startDist = 0;
    let startZoom = 1;
    let last = 0;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const onStart = (e) => {
      if (e.touches.length !== 2 || !zoomRef.current) return;
      e.preventDefault();
      startDist = dist(e.touches);
      startZoom = zoomRef.current.value ?? zoomRef.current.min;
    };
    const onMove = (e) => {
      const z = zoomRef.current;
      if (e.touches.length !== 2 || !z || !startDist) return;
      e.preventDefault();
      const now = Date.now();
      if (now - last < 60) return;
      last = now;
      const step = z.step || 0.1;
      const raw = startZoom * (dist(e.touches) / startDist);
      const next = Math.min(z.max, Math.max(z.min, Math.round(raw / step) * step));
      if (Math.abs(next - (z.value ?? 0)) >= step / 2) onChangeRef.current(next);
    };
    const onEnd = (e) => { if (e.touches.length < 2) startDist = 0; };
    el.addEventListener("touchstart", onStart, { passive: false });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, [enabled, hasZoom]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** Ignore multi-touch in tap-to-focus handlers (pinch is handled separately). */
export const isMultiTouch = (evt) => !!(evt?.touches && evt.touches.length > 1);
